class VariantSelects extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {};

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = parseJson(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Selectors
    this.media = document.querySelector('product-media-grid');
    this.media_mobile = document.querySelector('[data-product-media-mobile]');

    // Cache media data
    if (this.media && this.options.variant && this.options.handle) {
      this.mediaData = [
        {
          id: parseInt(this.options.variant),
          html: this.media.innerHTML,
          html_mobile: this.media_mobile.innerHTML,
        },
      ];
    }

    // Variant change listener
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.toggleButtonLoading(true);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.renderStickyPrice();
      this.dispatchVariantChange();
    }
  }

  updateOptions() {
    this.variantOptions = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.variantOptions[index]) {
            return true;
          }
          return this.variantOptions[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  async updateMedia() {
    if (!this.currentVariant || !this.media || !this.options.variant || !this.options.handle) {
      return;
    }
    const { html, html_mobile } = await this.getMedia(this.currentVariant.id);

    // Prevent reloading the dom if images are the same
    // Mobile is not checked due to different swipper id generated
    if (this.media.innerHTML != html) {
      this.media.innerHTML = html;
      this.media_mobile.innerHTML = html_mobile;

      // Init component to listen to new events
      const mediaGrid = document.querySelector('product-media-grid');
      if (mediaGrid) {
        // in the quick shop popup the media grid is a web component
        mediaGrid.init?.();
      }

      window.ProductModel?.loadShopifyXR();
    }
  }

  async getMedia(id) {
    // Check for cached data
    const item = this.mediaData.find((item) => item.id == parseInt(id));

    // Fetch data if not in cache
    if (!item) {
      let data = await fetch(
        `/products/${this.options.handle}?view=media&variant=${id}`,
        this.fetchConfig()
      );
      data = await data.text();
      const dom = new DOMParser().parseFromString(data, 'text/html');
      const html = dom.querySelector('product-media-grid').innerHTML;
      const html_mobile = dom.querySelector('[data-product-media-mobile]').innerHTML;

      const mediaJson = dom.querySelector('[id^="ProductJSON-"]');
      if (mediaJson) {
        document.body.appendChild(mediaJson);
      }

      // Cache fetch
      this.mediaData.push({
        id: id,
        html: html,
        html_mobile: html_mobile,
      });

      return { html, html_mobile };
    }

    return item;
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const ids = [
          [`price-${this.dataset.section}`],
          [`badges-${this.dataset.section}`],
          [`sticky-price-${this.dataset.section}`],
          [`product-back-in-stock-${this.dataset.section}`, true],
          [`product-recommended-product-${this.dataset.section}`, true],
          [`product-shipping-${this.dataset.section}`, true],
          [`product-form-${this.dataset.section}`],
        ];
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const updatedProductFormConfig = parseJson(
          html.querySelector('[data-product-form-config').textContent || '{}'
        );
        const productFormCustomElements = document.querySelectorAll(
          'product-form[data-buy-form-for-variant]'
        );

        productFormCustomElements.forEach((productForm) => {
          productForm.setAttribute('data-config', JSON.stringify(updatedProductFormConfig));
        });

        ids.forEach(([id, force]) => {
          const destination = document.getElementById(id);
          const source = html.getElementById(id);

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          } else if (destination && force && !source) {
            destination.innerHTML = '';
          }
        });

        this.updateWarrantyBlock(html);

        // handle shipping info
        const oldShipping = document.getElementById(`product-shipping-${this.dataset.section}`);
        const newShipping = html.getElementById(`product-shipping-${this.dataset.section}`);

        if (oldShipping && newShipping) {
          oldShipping.classList.remove('hidden');
          oldShipping.innerHTML = newShipping.innerHTML;
        } else if (!newShipping) {
          oldShipping?.classList.add('hidden');
        }

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
          this.toggleButtonLoading(false);
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;

        // Update price per variant
        const pricePerUnitId = `#product-variant-unit-price-${this.dataset.section}`;
        const pricePerUnitDestination = document.querySelector(pricePerUnitId);
        const pricePerUnitSource = html.querySelector(pricePerUnitId);
        if (pricePerUnitDestination && pricePerUnitSource) {
          pricePerUnitDestination.innerHTML = pricePerUnitSource.innerHTML || '';
        }
      });
  }

  updateWarrantyBlock(html) {
    const selector = `product-info warranty-block`;
    const container = document.querySelector('[data-warranty-block-container]');
    if (!container) return;

    const updated = html.querySelector(selector);

    if (!container) return;

    container.innerHTML = '';

    if (!updated) return;

    container.appendChild(updated);
  }

  renderStickyPrice() {
    const stickySubheader = document.querySelector('product-sticky-header');

    if (!stickySubheader) return;

    fetch(
      `${stickySubheader.dataset.url}?variant=${this.currentVariant.id}&section_id=${stickySubheader.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const ids = [`sticky-price-${stickySubheader.dataset.section}`];
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        ids.forEach((id) => {
          const destination = document.getElementById(id);
          const source = html.getElementById(id);

          if (source && destination) destination.innerHTML = source.innerHTML;
        });
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disabled
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButtons = [document.querySelector('[data-sticky-add-to-cart]')];
    addButtons.forEach((addButton) => {
      const addButtonText = addButton?.querySelector('[data-add-to-cart-button-text]');

      if (!addButton || !addButtonText) return;

      const isSticky = addButton.hasAttribute('data-sticky-add-to-cart');

      if (disable) {
        addButton.setAttribute('disabled', true);
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = isSticky
          ? window.variantStrings.stickyAddToCart
          : window.variantStrings.addToCart;
      }
    });

    if (!modifyClass) return;
  }

  toggleButtonLoading(loading = true) {
    const addButtons = [
      document
        .getElementById(`product-form-${this.dataset.section}`)
        ?.querySelector('[name="add"]'),
      document.querySelector('[data-sticky-add-to-cart]'),
    ];

    addButtons.forEach((addButton) => {
      if (!addButton) return;

      if (loading) {
        addButton.classList.add('loading');
      } else {
        addButton.classList.remove('loading');
      }
    });
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || parseJson(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this.updateOptions();
    this.updateMasterId();
    this.highlightOptions();
  }

  onVariantChange() {
    super.onVariantChange();
    this.highlightOptions();
  }

  updateOptions() {
    const optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));
    this.variantOptions = optionGroups.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  // Fetch config
  fetchConfig() {
    return {
      method: 'GET',
      headers: { 'Content-Type': 'text/html' },
    };
  }

  /*
   * Check for each option value if the other one/two selected variant options are available
   */
  highlightOptions() {
    // If no selected element make all available
    let selectedElement = event
      ? event.target
      : this.querySelector('[data-option-group] input[checked]');
    if (!selectedElement) {
      this.querySelectorAll('[data-option-group]').forEach((option) => {
        option.querySelectorAll('[data-option]').forEach((optionEl) => {
          let label = optionEl.querySelector('[data-option-label]');
          label.classList.remove('option__label--disabled');
        });
      });
      return;
    }

    // Loop over option groups
    this.querySelectorAll('[data-option-group]').forEach((option) => {
      // Get index from option group (1, 2 or 3)
      const optionIndex = parseInt(option.dataset.optionGroup);

      // Create compare options from group, remove selected option based on index
      const compareOptions = [...this.variantOptions].filter(
        (option, index) => index != optionIndex
      );

      // Loop over possible values for option group
      option.querySelectorAll('[data-option]').forEach((optionEl) => {
        let label = optionEl.querySelector('[data-option-label]'),
          input = optionEl.querySelector('[data-option-input]'),
          value = input.value;

        // Match option availability and check if the two current options are available for value
        let match = this.getVariantData().filter((variant) => {
          // Get available variants where the current option index is the option value
          if (variant.options[optionIndex] == value && variant.available) {
            // Create variant options, remove option based on option value
            const variantOptions = [...variant.options].filter(
              (option, index) => index != optionIndex
            );

            // Check if compare options is the same as variant options
            return variantOptions.every(
              (item, index) => item.toLowerCase() === compareOptions[index].toLowerCase()
            );
          }

          return false;
        });

        // Debug current index, current value, compare options and match
        // console.log(optionIndex, value, compareOptions, match);

        // Enable disable variant options
        label.classList.toggle('option__label--disabled', match.length == 0);
      });
    });
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}
